import type {
  CoachingGoal,
  CoachingGoalStatus,
} from '@efdigital/ll-addon-data';

const TRACKABLE: CoachingGoalStatus[] = ['active', 'achieved'];

export interface GoalProgressSummary {
  averagePercent: number;
  goalsComplete: number;
  goalsRemaining: number;
  totalGoals: number;
  milestonesDone: number;
  milestonesRemaining: number;
  totalMilestones: number;
}

export interface GoalCardProgress {
  percent: number;
  completedMilestones: number;
  totalMilestones: number;
}

interface MilestoneProgressSource {
  completed: boolean;
}

/** Goals the tracker counts — active or achieved, not dropped. */
export function trackableGoals(
  goals: readonly CoachingGoal[] | null | undefined
): CoachingGoal[] {
  return (goals ?? []).filter((goal) =>
    TRACKABLE.includes(goal.status ?? 'active')
  );
}

/**
 * Summarises overall goal progress for the tracker donut.
 *
 * Each active or achieved goal counts equally in the average. When a goal has
 * milestones, its percent is completed / total; otherwise achieved is 100% and
 * active is 0%. Dropped goals are ignored.
 *
 * Milestone totals only count milestones that were actually set — goals
 * without milestones are not treated as remaining milestones.
 */
export function goalProgressSummary(
  goals: readonly CoachingGoal[] | null | undefined,
  milestonesFor?: (
    goalId: string
  ) => readonly MilestoneProgressSource[] | null | undefined
): GoalProgressSummary {
  const trackable = trackableGoals(goals);
  let percentSum = 0;
  let goalsComplete = 0;
  let milestonesDone = 0;
  let milestonesRemaining = 0;

  for (const goal of trackable) {
    const goalId = goal.id == null ? null : String(goal.id);
    const milestones =
      goalId && milestonesFor ? milestonesFor(goalId) : undefined;
    const progress = goalCardProgress(goal, milestones);

    percentSum += progress.percent;

    const complete = goal.status === 'achieved';

    if (complete) {
      goalsComplete += 1;
    }

    if (milestones?.length) {
      milestonesDone += progress.completedMilestones;
      milestonesRemaining +=
        progress.totalMilestones - progress.completedMilestones;
    }
  }

  const totalGoals = trackable.length;

  return {
    averagePercent: totalGoals ? Math.round(percentSum / totalGoals) : 0,
    goalsComplete,
    goalsRemaining: totalGoals - goalsComplete,
    totalGoals,
    milestonesDone,
    milestonesRemaining,
    totalMilestones: milestonesDone + milestonesRemaining,
  };
}

/** Segmented bar slots — `true` means complete. Caps at ten when there are more milestones. */
export function progressSegments(
  summary: Pick<GoalProgressSummary, 'milestonesDone' | 'totalMilestones'>,
  maxSegments = 10
): boolean[] {
  const { milestonesDone, totalMilestones } = summary;

  if (totalMilestones === 0) {
    return [];
  }

  if (totalMilestones <= maxSegments) {
    return Array.from(
      { length: totalMilestones },
      (_, index) => index < milestonesDone
    );
  }

  const filled = Math.round((milestonesDone / totalMilestones) * maxSegments);

  return Array.from({ length: maxSegments }, (_, index) => index < filled);
}

/**
 * Per-goal progress for the goal card donut and milestone line.
 */
export function goalCardProgress(
  goal: CoachingGoal,
  milestones?: readonly MilestoneProgressSource[] | null
): GoalCardProgress {
  if (milestones?.length) {
    const completed = milestones.filter(
      (milestone) => milestone.completed
    ).length;

    return {
      percent: Math.round((completed / milestones.length) * 100),
      completedMilestones: completed,
      totalMilestones: milestones.length,
    };
  }

  if (goal.status === 'achieved') {
    return {
      percent: 100,
      completedMilestones: 1,
      totalMilestones: 1,
    };
  }

  return {
    percent: 0,
    completedMilestones: 0,
    totalMilestones: 0,
  };
}

export function milestoneStatusLine(progress: GoalCardProgress): string | null {
  if (progress.totalMilestones === 0) {
    return null;
  }

  return `${progress.completedMilestones} of ${progress.totalMilestones} milestones complete`;
}

/**
 * A target date is a calendar day, stored as UTC midnight. Formatting it in the
 * viewer's zone shifts it a day west of Greenwich, so read it back in UTC.
 */
export function formatGoalTargetDate(
  iso: string | null | undefined
): string | null {
  if (!iso) {
    return null;
  }

  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}
